import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnDestroy,
  Optional,
  ViewEncapsulation,
} from '@angular/core';
import {
  Router,
} from '@angular/router';
import {
  select,
  Store,
} from '@ngrx/store';
import {
  computeItemIdentifier,
  O3rComponent,
} from '@o3r/core';
import {
  RequestParametersService,
} from '@o3r/dynamic-content';
import {
  Localization,
  Translatable,
} from '@o3r/localization';
import {
  LinkableToRuleset,
  RulesEngineRunnerService,
} from '@o3r/rules-engine';
import {
  HeroComponents,
  RequestParams,
} from '@refx/shared-common';
import {
  ClearDeeplinkChallenge,
  DeeplinkChallenge,
  selectDeeplinkChallenge,
  UiStatusStore,
} from '@refx/shared-store';
import {
  Observable,
} from 'rxjs';
import {
  filter,
  map,
  take,
} from 'rxjs/operators';
import {
  COMPONENTS_CONFIGURATION_NAME,
} from '../../../configuration/dynamic-configuration';
import {
  AppRoutes,
  RefundRoutes,
  SelfReaccommodationRoutes,
  SharedRoutes,
} from '../../../configuration/routes';
import {
  ChallengeTranslation,
  translations,
} from './challenge.translation';

@O3rComponent({
  componentType: 'Page'
})
@Component({
  selector: 'refx-servicing-challenge',
  styleUrls: ['./challenge.style.scss'],
  templateUrl: './challenge.template.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class ChallengeComponent implements Translatable<ChallengeTranslation>, OnDestroy, LinkableToRuleset {
  private readonly componentName = computeItemIdentifier('ChallengeComponent', COMPONENTS_CONFIGURATION_NAME);

  /**
   * Localization of the component
   */
  @Input()
  @Localization('./challenge.localization.json')
  public translations: ChallengeTranslation;

  /**
   * Observable for the page title translation
   */
  public pageTitle$: Observable<string | undefined>;

  constructor(
    private readonly router: Router,
    private readonly store: Store<UiStatusStore>,
    private readonly requestParametersService: RequestParametersService,
    @Optional() private readonly rulesEngineService?: RulesEngineRunnerService
  ) {
    this.translations = translations;
    this.pageTitle$ = this.store.pipe(
      select(selectDeeplinkChallenge),
      take(1),
      map((challengeParameters) => this.getPageTitle(challengeParameters))
    );
    if (this.rulesEngineService) {
      this.rulesEngineService.enableRuleSetFor(this.componentName);
    }
  }

  /**
   * Get the page title translation based on the deeplink target
   *
   * @param challengeParameters the deeplink challenge parameters
   */
  private getPageTitle(challengeParameters?: DeeplinkChallenge): string | undefined {
    if (challengeParameters?.target) {
      switch (challengeParameters?.target) {
        case HeroComponents.Availability:
        case HeroComponents.Refund: {
          return this.translations.viewTripTitle;
        }
      }
    }
  }

  /**
   * Handle the navigation to the corresponding deeplink target
   *
   * @param challengeSucceeded boolean to indicate if the challenge succeeded
   */
  public handleNavigation(challengeSucceeded: boolean) {
    if (challengeSucceeded) {
      this.store.pipe(
        select(selectDeeplinkChallenge),
        take(1),
        map((deeplinkParameters) => deeplinkParameters?.target),
        filter<HeroComponents>((target) => !!target)
      ).subscribe((target) => {
        this.store.dispatch(ClearDeeplinkChallenge());
        this.requestParametersService.clearQueryParameters([RequestParams.encrypt]);
        switch (target) {
          case HeroComponents.Availability: {
            return this.router.navigateByUrl(`${AppRoutes.selfReaccommodation}/${SelfReaccommodationRoutes.availability}`);
          }
          case HeroComponents.Refund: {
            return this.router.navigateByUrl(`${AppRoutes.refund}/${RefundRoutes.review}`);
          }
          default: {
            return this.router.navigateByUrl(SharedRoutes.recovery);
          }
        }
      });
    }
  }

  public ngOnDestroy() {
    if (this.rulesEngineService) {
      this.rulesEngineService.disableRuleSetFor(this.componentName);
    }
  }
}
